import axios from "axios";
import { getBackendUrl } from "./backendUrl";

// Local/staging can configure a separate API origin. Production may omit the
// variable when a reverse proxy serves frontend and API from one domain.
const backendOrigin = getBackendUrl();
const api = axios.create({
  baseURL: `${backendOrigin}/api`,
  withCredentials: true,
  timeout: 60000,  // 60s — production cold-start with 134 holdings + day_change pre-warm can exceed 30s
});

// Auth interceptor — if session expires mid-session, surface it instead of silent retries
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    // Don't reload the page for /auth/me probes (AuthGate handles those)
    if (status === 401 && !url.includes("/auth/")) {
      // Fire a custom event so AuthGate can show login — avoids infinite retry loops
      try { window.dispatchEvent(new CustomEvent("capital360:auth-expired")); } catch {}
    }
    return Promise.reject(err);
  }
);

// Auth
export const authMe = () => api.get("/auth/me");
export const authLogin = (username, password) => api.post("/auth/login", { username, password });
export const authLogout = () => api.post("/auth/logout");

// Accounts
export const getAccounts = () => api.get("/accounts");
export const createAccount = (data) => api.post("/accounts", data);
export const deleteAccount = (id) => api.delete(`/accounts/${id}`);
export const disconnectBroker = (id) => api.post(`/broker/${id}/disconnect`);

// Broker OAuth
export const getZerodhaLogin = (accountId) => api.get(`/broker/zerodha/login/${accountId}`);
export const getBreezeLogin = (accountId) => api.get(`/broker/breeze/login/${accountId}`);
export const angelLogin = (data) => api.post("/broker/angel/login", data);

// Portfolio
export const getCombinedPortfolio = () => api.get("/portfolio/combined");
export const getPortfolioByCountry = (country) => api.get(`/portfolio/by-country/${country}`);
export const getPortfolioByAccount = (accountId) => api.get(`/portfolio/by-account/${accountId}`);
export const getPortfolioSectors = () => api.get("/portfolio/sectors");
export const getPortfolioPerformance = (range = "1M") => api.get(`/portfolio/performance?range=${range}`);

// Import
export const importMutualFunds = (file, mode = "replace") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("mode", mode);
  return api.post("/import/mutual-funds", fd);
};
export const importMutualFundsPdf = (file, password, mode = "replace") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("password", password);
  fd.append("mode", mode);
  return api.post("/import/mutual-funds-pdf", fd);
};
export const previewMfCentralCas = (file, password, ownerId = "aman") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("password", password);
  fd.append("owner_id", ownerId);
  return api.post("/import/mfcentral/cas/preview", fd);
};
export const applyMfCentralCas = (batchId) =>
  api.post(`/import/mfcentral/batches/${batchId}/apply`);
export const getMfCentralBatches = (limit = 20) =>
  api.get(`/import/mfcentral/batches?limit=${limit}`);
export const importUSStocks = (file, mode = "replace", accountId = "us-vested-aman") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("mode", mode);
  fd.append("account_id", accountId);
  return api.post("/import/us-stocks", fd);
};
export const getImportHistory = () => api.get("/import/history");
export const getUSStockAccounts = () => api.get("/accounts/us-stocks");

// Sync
export const getSyncHistory = () => api.get("/sync/history");
export const getSyncStatus = () => api.get("/sync/status");

// Insights
export const getPortfolioScore = () => api.get("/insights/score");
export const analyzePortfolio = (question) => api.post("/insights/analyze", { question });

// Signals & Decisions
export const getStockSignals = (symbol) => api.get(`/signals/${symbol}`);
export const getAllDecisions = () => api.get("/decisions/all");
export const getAISentiment = (symbol) => api.post(`/decisions/ai-sentiment/${symbol}`);
export const getHoldingScores = () => api.get("/portfolio/holding-scores");

// Mappings
export const getICICIMappings = () => api.get("/mappings/icici");
export const updateICICIMapping = (data) => api.post("/mappings/icici", data);
export const updateICICIMappingsBulk = (mappings) => api.post("/mappings/icici/bulk", mappings);

// AI Ideas Engine
export const getIdeas = () => api.get("/ideas");
export const refreshIdeas = (numPicks = 15, swapDelta = 10) =>
  api.post(`/ideas/refresh?num_picks=${numPicks}&swap_delta=${swapDelta}`);

// Rules-based Indian quality momentum / 52-week-high strategy
export const getMomentumStrategy = () => api.get("/momentum-strategy");
export const refreshMomentumStrategy = (limit = 20) => api.post(`/momentum-strategy/refresh?limit=${limit}`, {}, { timeout: 180000 });
export const refreshMomentumPositions = () => api.post("/momentum-strategy/positions/refresh", {}, { timeout: 120000 });
export const addMomentumPosition = (data) => api.post("/momentum-strategy/positions", data);
export const exitMomentumPosition = (id) => api.post(`/momentum-strategy/positions/${id}/exit`);

// Daily three-factor momentum analysis (10:00 Asia/Kolkata on NSE trading days)
export const getMomentumAnalysis = () => api.get("/momentum-analysis");
export const refreshMomentumAnalysis = () => api.post("/momentum-analysis/refresh", {}, { timeout: 600000 });
export const getMomentumAnalysisHistory = (limit = 14) => api.get(`/momentum-analysis/history?limit=${limit}`);
export const updateMomentumAnalysisSettings = (data) => api.put("/momentum-analysis/settings", data);

// Isolated Zerodha sleeve for the India Momentum Analysis model.
export const getMomentumSleeve = () => api.get("/momentum-sleeve");
export const updateMomentumSleeve = (data) => api.put("/momentum-sleeve", data);
export const addMomentumSleeveCapital = (data) => api.post("/momentum-sleeve/capital", data);
export const getMomentumSleeveRecoveryPreview = () => api.get("/momentum-sleeve/recovery-preview", { timeout: 120000 });
export const recoverMomentumSleeve = (data) => api.post("/momentum-sleeve/recover", data, { timeout: 120000 });
export const previewMomentumSleeve = () => api.post("/momentum-sleeve/preview", {}, { timeout: 120000 });
export const previewOneShareMomentumSleeveTest = () => api.post("/momentum-sleeve/test-preview", {}, { timeout: 120000 });
export const executeMomentumSleevePlan = (planId, confirmationPassword) => api.post(
  `/momentum-sleeve/plans/${planId}/execute`,
  { confirmation_password: confirmationPassword },
  { timeout: 120000 },
);
export const syncMomentumSleeve = () => api.post("/momentum-sleeve/sync", {}, { timeout: 120000 });

// Daily Nasdaq-listed U.S. momentum analysis (10:00 Asia/Kolkata, latest completed U.S. session)
export const getUsMomentumAnalysis = () => api.get("/us-momentum-analysis");
export const refreshUsMomentumAnalysis = () => api.post("/us-momentum-analysis/refresh", {}, { timeout: 600000 });
export const getUsMomentumAnalysisHistory = (limit = 14) => api.get(`/us-momentum-analysis/history?limit=${limit}`);
export const updateUsMomentumAnalysisSettings = (data) => api.put("/us-momentum-analysis/settings", data);

// Swap execution tracker (P4 feedback loop)
export const recordSwapExecuted = (data) => api.post("/swaps/executed", data);
export const getSwapExecutions = () => api.get("/swaps/executed");
export const deleteSwapExecution = (id) => api.delete(`/swaps/executed/${id}`);

// Alerts (rule engine — new: % move & earnings reminders via Email + Telegram)
export const getAlerts = () => api.get("/alerts");
export const updateNotificationPreferences = (data) =>
  api.put("/alerts/notification-preferences", data);
export const createAlert = (data) => api.post("/alerts", data);
export const deleteAlert = (id) => api.delete(`/alerts/${id}`);
export const toggleAlert = (id, active) => api.post(`/alerts/${id}/toggle?active=${active}`);
export const getAlertHistory = (limit = 50) => api.get(`/alerts/history?limit=${limit}`);
export const runAlertsCheck = () => api.post(`/alerts/test`);
export const syncTelegram = () => api.post(`/alerts/telegram/sync`);
export const sendTelegramTest = () => api.post(`/alerts/telegram/test`);
export const autoSetupAlerts = (telegram = true) => api.post(`/alerts/auto-setup?telegram=${telegram}`);
export const deleteAutoAlerts = () => api.delete(`/alerts/auto-generated`);

// Sentiment / Tickertape
export const getStockSentiment = (symbol) => api.get(`/stock/${symbol}/sentiment`);
export const getMarketMMI = () => api.get(`/market/mmi`);

// News & Pulse
export const getStockNews = (symbol, country = "India", limit = 12) =>
  api.get(`/stock/${symbol}/news?country=${country}&limit=${limit}`);
export const getMarketPulse = (perSource = 8) => api.get(`/market/pulse?limit_per_source=${perSource}`);
export const getMorningSummary = (force = false) => api.get(`/market/morning-summary?force=${force}`);
export const sendMorningToTelegram = () => api.post(`/market/morning-summary/send-telegram`);
export const getCorporateActions = (windowDays = 60) => api.get(`/portfolio/corporate-actions?window_days=${windowDays}`);
export const getDividendYield = () => api.get(`/portfolio/dividend-yield`);
export const getPassiveIncomeSummary = () => api.get(`/portfolio/passive-income-summary`);
export const getEveningWrap = (force = false) => api.get(`/market/evening-wrap?force=${force}`);
export const sendEveningToTelegram = () => api.post(`/market/evening-wrap/send-telegram`);
export const getClosingBell = (force = false) => api.get(`/market/closing-bell?force=${force}`);
export const sendClosingBellToTelegram = () => api.post(`/market/closing-bell/send-telegram`);
export const getPortfolioMovers = (limit = 10, country = "all", range = "1d") => api.get(`/portfolio/movers?limit=${limit}&country=${country}&range=${range}`);
export const getPriceOnlyPerformance = (range = "1M") => api.get(`/portfolio/performance-price-only?range=${range}`);

export const getConnectionHealth = () => api.get(`/broker/connection-health`);

export const getWeekendReview = (force = false) => api.get(`/market/weekend-review?force=${force}`);
export const sendWeekendToTelegram = () => api.post(`/market/weekend-review/send-telegram`);
export const sendWeekendToEmail = () => api.post(`/market/weekend-review/send-email`);

// Per-holding history (daily snapshots)
export const getHoldingHistory = (symbol, days = 90) => api.get(`/portfolio/holding-history/${symbol}?days=${days}`);
export const runSnapshot = () => api.post(`/admin/snapshot/run`);

// Buy Prices
export const setBuyPrice = (data) => api.post("/buy-prices", data);

// Rules
export const getRuleViolations = () => api.get("/rules/violations");
export const getRuleConfig = () => api.get("/rules/config");
export const updateRuleConfig = (data) => api.post("/rules/config", data);

// Portfolio Refresh
export const triggerPortfolioRefresh = () => api.post("/portfolio/refresh");
export const refreshPortfolioQuotes = () => api.post("/portfolio/quotes/refresh", {}, { timeout: 120000 });
export const getRefreshStatus = () => api.get("/portfolio/refresh-status");
export const getPortfolioZones = () => api.get("/portfolio/zones");
export const getPortfolioNews = () => api.get("/portfolio/news");

// Trade Journal
export const getJournalEntries = (limit = 50) => api.get(`/journal?limit=${limit}`);
export const createJournalEntry = (data) => api.post("/journal", data);
export const deleteJournalEntry = (id) => api.delete(`/journal/${id}`);

// Search & Watchlist
export const searchStocks = (q) => api.get(`/search?q=${encodeURIComponent(q)}`);
export const getWatchlist = () => api.get("/watchlist");
export const addToWatchlist = (symbol) => api.post(`/watchlist/${symbol}`);
export const removeFromWatchlist = (symbol) => api.delete(`/watchlist/${symbol}`);
export const getLivePrices = () => api.get("/live-prices");

// Manual Accounts
export const createManualAccount = (data) => api.post("/accounts/manual", data);
export const addManualHolding = (accountId, data) => api.post(`/accounts/manual/${accountId}/holdings`, data);
export const deleteManualHolding = (accountId, symbol) => api.delete(`/accounts/manual/${accountId}/holdings/${symbol}`);
export const importCsvToManual = (accountId, file, mode = "replace") => {
  const form = new FormData();
  form.append("file", file);
  return api.post(`/accounts/manual/${accountId}/import-csv?mode=${mode}`, form);
};
export const importHoldingsCsv = (accountId, file, mode = "replace") => {
  const form = new FormData();
  form.append("file", file);
  return api.post(`/accounts/manual/${accountId}/import-csv?mode=${mode}`, form);
};

// Stock Chat
export const sendStockChat = (symbol, message, sessionId) => api.post(`/stock/${symbol}/chat`, { message, session_id: sessionId, symbol });

// Portfolio Chat (context-aware AI advisor for the whole portfolio)
export const sendPortfolioChat = (message, sessionId) => api.post(`/portfolio/chat`, { message, session_id: sessionId });

// P&L breakdown per account/broker
export const getPnlBreakdown = () => api.get(`/portfolio/pnl-breakdown`);

// Dividends Dashboard
export const getDividends = () => api.get(`/portfolio/dividends`);

// Metal rates (gold/silver)
export const getMetalRates = () => api.get(`/metals/rates`);
export const refreshMetalPrices = () => api.post(`/assets/refresh-metals`);

// SGB lookup
export const sgbLookup = (symbol) => api.get(`/sgb/lookup`, { params: { symbol } });

// Manual holdings inline edit
export const listManualHoldings = (accountId) => api.get(`/accounts/manual/${accountId}/holdings`);
export const patchManualHolding = (accountId, symbol, patch) => api.patch(`/accounts/manual/${accountId}/holdings/${encodeURIComponent(symbol)}`, patch);
export const refreshAccountPrices = (accountId) => api.post(`/accounts/manual/${accountId}/refresh-prices`);
export const dedupAccounts = () => api.post(`/accounts/dedup`);

// Market Indices
export const getMarketIndices = () => api.get("/market-indices");

// Manual Assets
export const getManualAssets = (type) => api.get(`/assets${type ? `?asset_type=${type}` : ""}`);
export const createManualAsset = (data) => api.post("/assets", data);
export const updateManualAsset = (id, data) => api.put(`/assets/${id}`, data);
export const deleteManualAsset = (id) => api.delete(`/assets/${id}`);
export const uploadAssetPhoto = (id, formData) =>
  api.post(`/assets/${id}/photo`, formData, { headers: { "Content-Type": "multipart/form-data" } });
export const deleteAssetPhoto = (id) => api.delete(`/assets/${id}/photo`);
export const assetPhotoUrl = (id) => `${api.defaults.baseURL}/assets/${id}/photo`;
export const getNetWorthSummary = () => api.get("/assets/net-worth-summary");
export const getNetWorthHistory = () => api.get("/assets/net-worth-history");
export const getLiabilities = () => api.get("/liabilities");
export const createLiability = (data) => api.post("/liabilities", data);
export const updateLiability = (id, data) => api.put(`/liabilities/${id}`, data);
export const deleteLiability = (id) => api.delete(`/liabilities/${id}`);

// Crypto Portfolio
export const getCryptoHoldings = () => api.get("/crypto/holdings");
export const createCryptoHolding = (data) => api.post("/crypto/holdings", data);
export const updateCryptoHolding = (id, data) => api.patch(`/crypto/holdings/${id}`, data);
export const deleteCryptoHolding = (id) => api.delete(`/crypto/holdings/${id}`);
export const cryptoCoinSearch = (q) => api.get(`/crypto/coingecko/search?q=${encodeURIComponent(q)}`);
export const connectCoinDCX = (data) => api.post("/crypto/coindcx/connect", data);
export const syncCoinDCX = () => api.post("/crypto/coindcx/sync");
export const listCryptoExchanges = () => api.get("/crypto/exchanges");
export const disconnectCryptoExchange = (exchange) => api.delete(`/crypto/exchanges/${exchange}`);


// Mutual Fund Look-Through
export const getMFLookthroughList = () => api.get("/funds/lookthrough/list", {
  params: { _ts: Date.now() },
  headers: { "Cache-Control": "no-cache" },
});
export const getMFLookthroughExposure = () => api.get("/funds/lookthrough/exposure", {
  params: { _ts: Date.now() },
  headers: { "Cache-Control": "no-cache" },
});
export const deleteMFLookthrough = (fundSymbol) => api.delete(`/funds/lookthrough/${fundSymbol}`);
export const uploadMFLookthrough = (formData) => api.post("/funds/lookthrough/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const searchMFLookthrough = (q) => api.get(`/funds/lookthrough/search?q=${encodeURIComponent(q)}`);
export const autoFetchMFLookthrough = (payload) => api.post("/funds/lookthrough/auto-fetch", payload);
export const autoFetchMFLookthroughAll = () => api.post("/funds/lookthrough/auto-fetch-all");
export const autoFetchMFHoldingsAll = () => api.post("/funds/lookthrough/auto-fetch-holdings-all");
export const remapMFTickers = () => api.post("/funds/lookthrough/remap-tickers");
export const fetchMFLookthroughFromUrl = (payload) => api.post("/funds/lookthrough/fetch-from-url", payload);
export const getBookmarkletToken = () => api.get("/funds/lookthrough/bookmarklet-token");

// Guidance-only Portfolio Adviser (never places broker orders)
export const getAdviserProfile = () => api.get("/adviser/profile");
export const updateAdviserProfile = (data) => api.put("/adviser/profile", data);
export const getAdviserAnalysis = (newMoney = 0) => api.get(`/adviser/analysis?new_money=${encodeURIComponent(newMoney || 0)}`);

// Household liabilities, cash flow and financial goals
export const getPlanningSummary = () => api.get("/planning/summary");
export const createFinancialGoal = (data) => api.post("/planning/goals", data);
export const updateFinancialGoal = (id, data) => api.put(`/planning/goals/${id}`, data);
export const deleteFinancialGoal = (id) => api.delete(`/planning/goals/${id}`);
export const updateCashFlow = (data) => api.put("/planning/cash-flow", data);
export const createPlannedExpense = (data) => api.post("/planning/expenses", data);
export const updatePlannedExpense = (id, data) => api.put(`/planning/expenses/${id}`, data);
export const deletePlannedExpense = (id) => api.delete(`/planning/expenses/${id}`);

// Income Intelligence workbook preview, import and analytics
export const previewIncomeWorkbook = (formData) => api.post("/income/preview", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const importIncomeWorkbook = (formData) => api.post("/income/import", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const getIncomeAnalytics = () => api.get("/income/analytics", { params: { _ts: Date.now() } });

// Family profiles and source-preserving health records
export const getFamilyMembers = () => api.get("/family/members");
export const createFamilyMember = (data) => api.post("/family/members", data);
export const updateFamilyMember = (id, data) => api.put(`/family/members/${id}`, data);
export const getFamilyAttributionReview = (fromDate = "2025-04-01") => api.get("/family/finance/attribution-review", { params: { from_date: fromDate, _ts: Date.now() } });
export const updateFamilyTransactionAllocations = (transactionId, allocations) => api.put(`/family/finance/transactions/${encodeURIComponent(transactionId)}/allocations`, { allocations });
export const removeFamilyTransactionAllocations = (transactionId) => api.delete(`/family/finance/transactions/${encodeURIComponent(transactionId)}/allocations`);
export const getFamilyFinanceSummary = (fromDate = "2025-04-01") => api.get("/family/finance/summary", { params: { from_date: fromDate, _ts: Date.now() } });
export const getFamilyPlanningOverview = () => api.get("/family-planning/overview", { params: { _ts: Date.now() } });
export const getFamilyPlanningMember = (memberId) => api.get(`/family-planning/members/${encodeURIComponent(memberId)}`, { params: { _ts: Date.now() } });
export const getFamilyEducationReport = () => api.get("/family-planning/education-report", { params: { _ts: Date.now() } });
export const getFamilyPlanningNeeds = () => api.get("/family-planning/needs", { params: { _ts: Date.now() } });
export const createFamilyPlanningNeed = (data) => api.post("/family-planning/needs", data);
export const updateFamilyPlanningNeed = (id, data) => api.put(`/family-planning/needs/${encodeURIComponent(id)}`, data);
export const getFamilyPlanningRetirement = () => api.get("/family-planning/retirement", { params: { _ts: Date.now() } });
export const getFamilyPlanningAssumptions = () => api.get("/family-planning/assumptions", { params: { _ts: Date.now() } });
export const updateFamilyPlanningAssumptions = (data) => api.put("/family-planning/assumptions", data);
export const getFamilyHealth = (memberId) => api.get("/family/health", { params: { member_id: memberId, _ts: Date.now() } });
export const previewFamilyHealth = (formData) => api.post("/family/health/preview", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const importFamilyHealth = (formData) => api.post("/family/health/import", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const previewFamilyHealthPdf = (formData) => api.post("/family/health/documents/preview", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const importFamilyHealthPdf = (formData) => api.post("/family/health/documents/import", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const getFamilyHealthDocuments = (memberId) => api.get("/family/health/documents", { params: { member_id: memberId, _ts: Date.now() } });
export const familyHealthDocumentUrl = (documentId) => `${api.defaults.baseURL}/family/health/documents/${encodeURIComponent(documentId)}/file`;

// Tax history
export const getTaxHistory = () => api.get("/tax/history");
export const previewTaxDocuments = (formData) => api.post("/tax/preview", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const importTaxDocuments = (formData) => api.post("/tax/import", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const taxDocumentUrl = (documentId) => `${api.defaults.baseURL}/tax/documents/${encodeURIComponent(documentId)}/file`;
export const getFamilyHealthDocumentReview = (documentId) => api.get(`/family/health/documents/${encodeURIComponent(documentId)}/review`);
export const confirmFamilyHealthDocumentReview = (documentId, data) => api.post(`/family/health/documents/${encodeURIComponent(documentId)}/review`, data);

// Spending Intelligence statement-workbook preview, import and analytics
export const previewSpendingWorkbook = (formData) => api.post("/spending/preview", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const importSpendingWorkbook = (formData) => api.post("/spending/import", formData, { headers: { "Content-Type": "multipart/form-data" } });

// Statements — isolated staging and explicit approval ledger.
export const uploadStatement = (formData) => api.post("/statements/inbox/upload", formData, {
  headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
});
export const uploadHistoricalStatements = (formData) => api.post("/statements/historical/upload", formData, {
  headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
});
export const getStatementInbox = (params = {}) => api.get("/statements/inbox", { params });
export const getStatementBatch = (id) => api.get(`/statements/batches/${id}`);
export const updateStatementBatch = (id, data) => api.patch(`/statements/batches/${id}`, data);
export const updateStagedTransaction = (id, data) => api.patch(`/statements/transactions/${id}`, data);
export const bulkClassifyStatements = (id, data) => api.post(`/statements/batches/${id}/bulk-classify`, data);
export const addStatementReconciliationAdjustment = (id, data = {}) => api.post(`/statements/batches/${id}/reconciliation-adjustment`, data);
export const approveStatementBatch = (id) => api.post(`/statements/batches/${id}/approve`);
export const approveSelectedStatementTransactions = (id, transactionIds) => api.post(`/statements/batches/${id}/approve-selected`, { transaction_ids: transactionIds });
export const returnStatementToReview = (id) => api.post(`/statements/batches/${id}/return-to-review`);
export const rejectStatementBatch = (id) => api.post(`/statements/batches/${id}/reject`);
export const getApprovedStatements = (params = {}) => api.get("/statements/approved", { params });
export const getNiftyMonthlyBenchmark = (fiscalYearStart) => api.get("/statements/nifty-monthly", { params: { fiscal_year_start: fiscalYearStart } });
export const updateApprovedStatement = (id, data) => api.patch(`/statements/approved/${id}`, data);
export const getStatementHistory = () => api.get("/statements/history");
export const getStatementSources = () => api.get("/statements/sources");
export const createStatementAccount = (data) => api.post("/statements/accounts", data);
export const updateStatementAccount = (id, data) => api.patch(`/statements/accounts/${id}`, data);
export const deleteStatementAccount = (id) => api.delete(`/statements/accounts/${id}`);
export const createStatementManualEntry = (data) => api.post("/statements/manual-entry", data);
export const getStatementManualAccounts = (fiscalYearStart) => api.get("/statements/manual-accounts", { params: { fiscal_year_start: fiscalYearStart } });
export const updateStatementManualEntry = (id, data) => api.patch(`/statements/manual-entry/${id}`, data);
export const deleteStatementManualEntry = (id) => api.delete(`/statements/manual-entry/${id}`);
export const getStatementAnnualClose = (fiscalYearStart, periodKey = "annual") => api.get("/statements/annual-close", { params: { fiscal_year_start: fiscalYearStart, period_key: periodKey } });
export const getHomeDashboard = () => api.get("/home-dashboard/summary");
export const getBusinessOverview = (fiscalYearStart) => api.get("/business-intelligence/overview", { params: { fiscal_year_start: fiscalYearStart, _ts: Date.now() } });
export const updateHomeDashboardSignal = (signalId, payload) => api.patch(`/home-dashboard/signals/${signalId}`, payload);
export const refreshFundActivity = () => api.post("/home-dashboard/fund-activity/refresh");
export const getRelationshipSummary = () => api.get("/relationships/summary");
export const previewRelationshipImport = (formData) => api.post("/relationships/imports/preview", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const confirmRelationshipImport = (id) => api.post(`/relationships/imports/${id}/confirm`);
export const rollbackRelationshipImport = (id) => api.post(`/relationships/imports/${id}/rollback`);
export const getRelationshipImports = () => api.get("/relationships/imports");
export const getRelationshipContacts = (params = {}) => api.get("/relationships/contacts", { params });
export const getRelationshipContact = (id) => api.get(`/relationships/contacts/${id}`);
export const updateRelationshipContact = (id, data) => api.patch(`/relationships/contacts/${id}`, data);
export const archiveRelationshipContact = (id) => api.delete(`/relationships/contacts/${id}`);
export const getRelationshipMerges = (status = "pending") => api.get("/relationships/merges", { params: { status } });
export const approveRelationshipMerge = (id, data) => api.post(`/relationships/merges/${id}/approve`, data);
export const rejectRelationshipMerge = (id) => api.post(`/relationships/merges/${id}/reject`);
export const getRelationshipEnrichment = (status = "pending") => api.get("/relationships/enrichment", { params: { status } });
export const decideRelationshipEnrichment = (id, status) => api.patch(`/relationships/enrichment/${id}`, { status });
export const getRelationshipSignals = (status = "") => api.get("/relationships/signals", { params: { status } });
export const updateRelationshipSignal = (id, data) => api.patch(`/relationships/signals/${id}`, data);
export const createOutreachFromSignal = (id) => api.post(`/relationships/signals/${id}/outreach`);
export const getRelationshipOutreach = (status = "") => api.get("/relationships/outreach", { params: { status } });
export const createRelationshipOutreach = (data) => api.post("/relationships/outreach", data);
export const updateRelationshipOutreach = (id, data) => api.patch(`/relationships/outreach/${id}`, data);
export const addRelationshipInteraction = (data) => api.post("/relationships/interactions", data);
export const scanRelationshipNews = () => api.post("/relationships/news/scan", null, { timeout: 120000 });

// Travel planning, archive and expense reconciliation
export const getTravelOverview = () => api.get("/travel/overview");
export const getTravelTrips = (params = {}) => api.get("/travel/trips", { params });
export const getTravelTrip = (id) => api.get(`/travel/trips/${encodeURIComponent(id)}`);
export const createTravelTrip = (data) => api.post("/travel/trips", data);
export const updateTravelTrip = (id, data) => api.patch(`/travel/trips/${encodeURIComponent(id)}`, data);
export const deleteTravelTrip = (id) => api.delete(`/travel/trips/${encodeURIComponent(id)}`);
export const createTravelExpense = (tripId, data) => api.post(`/travel/trips/${encodeURIComponent(tripId)}/expenses`, data);
export const updateTravelExpense = (id, data) => api.patch(`/travel/expenses/${encodeURIComponent(id)}`, data);
export const saveStatementAnnualCloseSnapshot = (data) => api.post("/statements/annual-close/snapshots", data);
export const deleteStatementAnnualCloseSnapshot = (id) => api.delete(`/statements/annual-close/snapshots/${id}`);
export const updateStatementAnnualCloseStatus = (fiscalYearStart, data, periodKey = "annual") => api.patch(`/statements/annual-close/${fiscalYearStart}/status`, data, { params: { period_key: periodKey } });
export const getStatementRewards = (fiscalYearStart) => api.get("/statements/rewards", { params: { fiscal_year_start: fiscalYearStart } });
export const getRewardIntelligence = () => api.get("/statements/rewards/intelligence");
export const refreshRewardIntelligence = () => api.post("/statements/rewards/intelligence/refresh", null, { timeout: 120000 });
export const upsertRewardSnapshot = (data) => api.post("/statements/rewards/snapshots", data);
export const deleteRewardSnapshot = (id) => api.delete(`/statements/rewards/snapshots/${id}`);
export const createStatementCapitalEntry = (data) => api.post("/statements/capital-entry", data);
export const updateStatementCapitalEntry = (id, data) => api.patch(`/statements/capital-entry/${id}`, data);
export const previewAngelTaxPnl = (formData) => api.post("/statements/angel-tax-pnl/preview", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
export const getAngelTaxPnlImports = () => api.get("/statements/angel-tax-pnl/imports");
export const approveAngelTaxPnl = (id, rows) => api.post(`/statements/angel-tax-pnl/${id}/approve`, { rows });
export const getIciciCapitalGainImports = () => api.get("/statements/icici-capital-gains/imports");
export const getZerodhaTaxPnlImports = () => api.get("/statements/zerodha-tax-pnl/imports");
export const approveZerodhaTaxPnl = (id, rows) => api.post(`/statements/zerodha-tax-pnl/${id}/approve`, { rows });
export const getInvestmentValuations = (brokerAccountId) => api.get("/statements/investment-valuations", { params: brokerAccountId ? { broker_account_id: brokerAccountId } : {} });
export const getStatementMonthEndFxRate = (year, month) => api.get("/statements/fx-rate", { params: { year, month } });
export const scanDividendReconciliations = () => api.post("/statements/dividend-reconciliation/scan", null, { timeout: 120000 });
export const getDividendReconciliations = (status = "pending") => api.get("/statements/dividend-reconciliation", { params: { status } });
export const updateDividendReconciliation = (transactionId, data) => api.patch(`/statements/dividend-reconciliation/${transactionId}`, data);
export const saveStatementSource = (data) => api.post("/statements/sources", data);
export const disableStatementSource = (id) => api.delete(`/statements/sources/${id}`);
export const getMissingStatements = () => api.get("/statements/missing");
export const checkStatementEmail = () => api.post("/statements/email/check", null, { timeout: 120000 });
export const startGmailConnection = () => api.post("/statements/gmail/oauth/start");
export const disconnectGmail = () => api.delete("/statements/gmail/connection");
export const getMfCentralEmailSources = () => api.get("/statements/mfcentral/email-sources");
export const saveMfCentralEmailSource = (data) => api.post("/statements/mfcentral/email-sources", data);
export const getSpendingAnalytics = () => api.get("/spending/analytics", { params: { _ts: Date.now() } });
export const getMoneyIntelligence = (fiscalYearStart) =>
  api.get("/money-intelligence/summary", { params: { fiscal_year_start: fiscalYearStart, _ts: Date.now() } });
export const normalizeMoneyClassifications = (fiscalYearStart) =>
  api.post("/money-intelligence/normalize-classifications", null, { params: { fiscal_year_start: fiscalYearStart } });

// Confirmation-led household Financial Memory. Secrets are never accepted.
export const getFinancialMemorySummary = () => api.get("/financial-memory/summary");
export const getFinancialMemoryRecords = (params = {}) => api.get("/financial-memory/records", { params });
export const createFinancialMemoryRecord = (data) => api.post("/financial-memory/records", data);
export const updateFinancialMemoryRecord = (id, data) => api.put(`/financial-memory/records/${id}`, data);
export const confirmFinancialMemoryRecord = (id) => api.post(`/financial-memory/records/${id}/confirm`);
export const rejectFinancialMemoryRecord = (id) => api.post(`/financial-memory/records/${id}/reject`);
export const getFinancialMemoryAudit = (id) => api.get(`/financial-memory/records/${id}/audit`);
export const refreshFinancialMemoryProposals = () => api.post("/financial-memory/proposals/refresh");

export const previewSelectedMomentumOrders = (data) => api.post("/momentum-sleeve/order-preview", data);
